import { PersonDuplicates } from '@fs/zion-icon'
import {
  Button,
  Cell,
  DialogOverlay,
  DialogOverlayActions,
  DialogOverlayFooter,
  Grid,
  Select,
  TextField,
} from '@fs/zion-ui'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { createPerson } from '../../../../services/famousHistoricalService'
import DmcDataView from '../../../common/DmcDataView'
import { DmcDatePicker } from '../../../common/DmcDatePicker/DmcDatePicker'

const defaultPersonObject = {
  birthDateItem: { key: 'birthDateItem', primaryText: '' },
  deathDateItem: { key: 'deathDateItem', primaryText: '' },
}

export default function FamousCreatePersonDialog({ countries, ...overlay }) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const [person, setPerson] = useState(defaultPersonObject)
  const [country, setCountry] = useState()
  const [alternatePerson, setAlternatePerson] = useState()
  const data = getFamousDataForm()
  const firstNameTextBoxRef = useRef()

  useEffect(() => {
    if (overlay.isOpen && !overlay.transitioning) {
      firstNameTextBoxRef.current.focus()
    }
  }, [overlay.isOpen, overlay.transitioning])

  function shouldDisableSubmitButtons() {
    if (alternatePerson) {
      if (JSON.stringify(alternatePerson) === JSON.stringify(person)) {
        return true
      }
    }
    return (
      Object.keys(person).length === 2 && !person.birthDateItem?.formattedDate && !person.deathDateItem?.formattedDate
    )
  }

  const onPersonChange = (fieldType, newValue) => {
    const newPerson = { ...person, [fieldType]: newValue }
    if (newValue === '') {
      delete newPerson[fieldType]
    }
    setPerson(newPerson)
  }

  const submitBtnOnClickHandler = async (isSubmitBtn) => {
    const personWithFormattedDates = {
      ...person,
      birthDate: person.birthDateItem && person.birthDateItem.formattedDate,
      deathDate: person.deathDateItem && person.deathDateItem.formattedDate,
    }
    delete personWithFormattedDates.birthDateItem
    delete personWithFormattedDates.deathDateItem

    const personDto = {
      metaDataId: 1,
      personJSON: JSON.stringify(personWithFormattedDates),
      matchType: 'FAMOUS',
      country,
    }
    try {
      await createPerson(personDto)
      if (isSubmitBtn) {
        setPerson(defaultPersonObject)
        setAlternatePerson()
      } else {
        setAlternatePerson(JSON.parse(JSON.stringify(person)))
      }
      firstNameTextBoxRef.current.select()
    } catch (err) {
      error(`Error creating famous person: ${err.message || ''}`, err)
    }
  }
  const cancelBtnOnClickHandler = () => {
    setPerson(defaultPersonObject)
    setCountry()
    overlay.close()
  }

  function getFamousDataForm() {
    return [
      {
        key: 'famousFirstName',
        label: <b>{t('tools.lists.labels.firstName', 'First Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.firstName}
            onChange={(e) => onPersonChange('firstName', e.target.value)}
            ref={(element) => {
              firstNameTextBoxRef.current = element
            }}
          />
        ),
      },
      {
        key: 'famousSurname',
        label: <b>{t('name.parts.surname', 'Surname')}</b>,
        detail: (
          <TextField type="text" value={person.surname} onChange={(e) => onPersonChange('surname', e.target.value)} />
        ),
      },
      {
        key: 'famousCategory',
        label: <b>{t('category', 'Category')}</b>,
        detail: (
          <Select
            placeholder={t('tools.lists.placeholders.selectCategory', 'Select Category')}
            value={person.category}
            onChange={(e) => onPersonChange('category', e.target.value)}
            options={[
              {
                value: 'BIB',
                label: t('famous.category.biblical', 'Biblical'),
              },
              {
                value: 'ENT',
                label: t('famous.category.entertainment', 'Entertainment'),
              },
              {
                value: 'FIC',
                label: t('famous.category.fictional', 'fictional'),
              },
              {
                value: 'HIS',
                label: t('famous.category.historical', 'Historical'),
              },
              {
                value: 'HOL',
                label: t('famous.category.holocaust', 'Holocaust'),
              },
              {
                value: 'MIL',
                label: t('famous.category.military', 'Military'),
              },
              {
                value: 'OTH',
                label: t('famous.category.other', 'Other'),
              },
              {
                value: 'POL',
                label: t('famous.category.political', 'Political'),
              },
              {
                value: 'REL',
                label: t('famous.category.religious', 'Religious'),
              },
              {
                value: 'SPO',
                label: t('famous.category.sports', 'Sports'),
              },
              {
                value: 'TER',
                label: t('famous.category.terrorist', 'Terrorist'),
              },
              {
                value: 'TRG',
                label: t('famous.category.tragedy', 'Tragedy'),
              },
            ]}
          />
        ),
      },
      {
        key: 'famousBirthDate',
        label: null,
        detail: (
          <DmcDatePicker
            label={t('tools.lists.labels.birthDate', 'Birth Date')}
            selectedItem={person.birthDateItem}
            setSelectedItem={(e) => onPersonChange('birthDateItem', e)}
            dayMonthYearDateFormat="DD MMM YYYY"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        ),
      },
      {
        key: 'famousBirthPlace',
        label: <b>{t('birthPlace', 'Birth Place')}</b>,
        detail: (
          <TextField
            type="text"
            value={person?.birthPlace?.replaceAll('"', '')}
            onChange={(e) => onPersonChange('birthPlace', e.target.value)}
          />
        ),
      },
      {
        key: 'famousGender',
        label: <b>{t('gender.label', 'Gender')}</b>,
        detail: (
          <Select
            placeholder={t('tools.lists.placeholders.selectGender', 'Select Gender')}
            value={person.gender}
            onChange={(e) => onPersonChange('gender', e.target.value)}
            options={[
              { value: 'Male', label: t('gender.male', 'Male') },
              { value: 'Female', label: t('gender.female', 'Female') },
            ]}
          />
        ),
      },
      {
        key: 'famousDeathDate',
        label: null,
        detail: (
          <DmcDatePicker
            label={t('deathDate', 'Death Date')}
            selectedItem={person.deathDateItem}
            setSelectedItem={(e) => onPersonChange('deathDateItem', e)}
            dayMonthYearDateFormat="DD MMM YYYY"
            monthYearDateFormat="yyyy"
            yearDateFormat="yyyy"
          />
        ),
      },
      {
        key: 'famousDeathPlace',
        label: <b>{t('deathPlace', 'Death Place')}</b>,
        detail: (
          <TextField
            type="text"
            value={person?.deathPlace?.replaceAll('"', '')}
            onChange={(e) => onPersonChange('deathPlace', e.target.value)}
          />
        ),
      },
      {
        key: 'famousBiography',
        label: <b>{t('biography', 'Biography')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.biography}
            onChange={(e) => onPersonChange('biography', e.target.value)}
          />
        ),
      },
      {
        key: 'famousParent1GivenName',
        label: <b>{t('tools.lists.labels.parent1GivenName', 'Parent #1 Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.parent1GivenName}
            onChange={(e) => onPersonChange('parent1GivenName', e.target.value)}
          />
        ),
      },
      {
        key: 'famousParent1Surname',
        label: <b>{t('tools.lists.labels.parent1Surname', 'Parent #1 Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.parent1Surname}
            onChange={(e) => onPersonChange('parent1Surname', e.target.value)}
          />
        ),
      },
      {
        label: null,
        detail: null,
        key: 'spacer1',
      },
      {
        key: 'famousParent2GivenName',
        label: <b>{t('tools.lists.labels.parent2GivenName', 'Parent #2 Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.parent2GivenName}
            onChange={(e) => onPersonChange('parent2GivenName', e.target.value)}
          />
        ),
      },
      {
        key: 'famousParent2Surname',
        label: <b>{t('tools.lists.labels.parent2Surname', 'Parent #2 Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.parent2Surname}
            onChange={(e) => onPersonChange('parent2Surname', e.target.value)}
          />
        ),
      },
      {
        label: null,
        detail: null,
        key: 'spacer2',
      },
      {
        key: 'famousSpouseGivenName',
        label: <b>{t('tools.lists.labels.spouseGivenName', 'Spouse Given Name')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.spouseGivenName}
            onChange={(e) => onPersonChange('spouseGivenName', e.target.value)}
          />
        ),
      },
      {
        key: 'famousSpouseSurname',
        label: <b>{t('tools.lists.labels.spouseSurname', 'Spouse Surname')}</b>,
        detail: (
          <TextField
            type="text"
            value={person.spouseSurname}
            onChange={(e) => onPersonChange('spouseSurname', e.target.value)}
          />
        ),
      },
    ]
  }

  return (
    <DialogOverlay
      {...overlay}
      title={t('tools.lists.headings.createNewFamousPerson', 'Create New Famous Person')}
      size="xxl"
    >
      <Grid>
        <Cell columns={3}>
          <Select
            id="country"
            placeholder={t('tools.lists.placeholders.selectMultiAreaTeam', 'Select Multi-area Team')}
            label={t('tools.lists.labels.multiAreaTeam', 'Multi-area Team')}
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            options={countries}
          />
        </Cell>
        <p columns={9} />
      </Grid>
      <DmcDataView data={data} />

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={() => submitBtnOnClickHandler(true)} disabled={shouldDisableSubmitButtons()}>
            {t('action.submit', 'Submit')}
          </Button>
          <Button
            Icon={PersonDuplicates}
            emphasis="high"
            onClick={() => submitBtnOnClickHandler(false)}
            disabled={shouldDisableSubmitButtons()}
          >
            {t('name.alternate', 'Alternate Name')}
          </Button>
          <Button emphasis="medium" onClick={cancelBtnOnClickHandler}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}
